import { getSupportConfigFromSupportConfigsAndCheck } from '@editorUtils'
import { isEqual } from 'lodash-es'
import { MultiEditCheckSettingsElement } from '../MultiEditCheckSettings/types'

/**
 * The support fields the support compression multi-edit can unify, in the order
 * the section shows them.
 */
export const SUPPORT_COMPRESSION_FIELDS = ['area', 'overhang', 'k_c_90', 'f_c_90_k'] as const

export type SupportCompressionField = typeof SUPPORT_COMPRESSION_FIELDS[number]

/** Plain-text field names for the warning; the field blocks render styled labels. */
export const supportCompressionFieldLabels: Record<SupportCompressionField, string> = {
  area: 'Geom. Auflager',
  overhang: 'Überstand',
  k_c_90: 'kc,90',
  f_c_90_k: 'fc,90,k',
}

export type SupportCompressionFieldValue = RectangularSupportArea | Overhang | number | null

/** Support indices into a member's `support_configs`, keyed by element guid. */
export type SupportIndicesByElement = Record<string, number[]>

/**
 * The staged shape of the support compression multi-edit. One entry per support
 * field the whole selection agrees on (or that „Ändern" staged); a field the
 * supports still differ on stays absent and is never written on save. `null`
 * means "no explicit value given" — the backend then derives the value per
 * support — and is a stageable value in its own right.
 */
export interface SharedSupportCompressionSettings {
  area?: RectangularSupportArea | null
  overhang?: Overhang | null
  k_c_90?: number
  f_c_90_k?: number | null
  /** Supports the user took out of the edit; toggled from the matrix. */
  excludedSupports?: SupportIndicesByElement
  /** Supports that are no real supports — see {@link buildIrrelevantSupports}. */
  irrelevantSupports?: SupportIndicesByElement
}

/** Membership test for both support index maps of the staged settings. */
export const containsSupportIndex = (
  indices: SupportIndicesByElement | undefined,
  elementGuid: string,
  supportIndex: number,
): boolean => !!indices?.[elementGuid]?.includes(supportIndex)

/** Removes a support from the edit, or adds a removed one back. */
export const toggleExcludedSupport = (
  excludedSupports: SupportIndicesByElement | undefined,
  elementGuid: string,
  supportIndex: number,
): SupportIndicesByElement => {
  const current = excludedSupports?.[elementGuid] ?? []
  const next = current.includes(supportIndex)
    ? current.filter(index => index !== supportIndex)
    : [...current, supportIndex].sort((a, b) => a - b)

  const result = { ...excludedSupports }
  if (next.length === 0) delete result[elementGuid]
  else result[elementGuid] = next

  return result
}

/**
 * True when the member carries per-support settings, i.e. it is a timber or
 * timber-slab member. Steel members configure their support via the „Fußpunkt"
 * section instead.
 */
export const hasSupportCompressionSettings = (
  settings: SettingsOnMember | undefined,
): settings is TimberCheckSettings | TimberSlabCheckSettings =>
  settings != null &&
  (settings.setting_type === 'timber' || settings.setting_type === 'timber-slab') &&
  Array.isArray(settings.support_configs)

export const getSupportConfigs = (
  settings: SettingsOnMember | undefined,
): SupportConfig[] | undefined =>
  hasSupportCompressionSettings(settings) ? settings.support_configs : undefined

const isSupportCompressionCheck = (
  check: CombinedPositionCheck,
): check is SupportCompressionStructuralCheck => check.check_type === 'SupportCompression'

/**
 * Maps one member's support compression checks onto its support configs, index
 * i of the result belonging to support_configs[i]. Matched by nearest relative
 * position via {@link getSupportConfigFromSupportConfigsAndCheck} — the backend's
 * check positions carry float noise against the config positions (see
 * https://app.clickup.com/t/2590559/MDG-3506?comment=90150108322599&threadedComment=90150108328170).
 * Supports without a computable check stay undefined.
 */
export const mapSupportCompressionChecks = (
  checks: CombinedPositionCheck[],
  supportConfigs: SupportConfig[],
): (SupportCompressionStructuralCheck | undefined)[] => {
  const result: (SupportCompressionStructuralCheck | undefined)[] = supportConfigs.map(
    () => undefined,
  )

  for (const check of checks) {
    if (!isSupportCompressionCheck(check)) continue

    const match = getSupportConfigFromSupportConfigsAndCheck(supportConfigs, check)
    if (!match) continue

    const existing = result[match.supportConfigIndex]
    if (!existing || check.max_utilization > existing.max_utilization) {
      result[match.supportConfigIndex] = check
    }
  }

  return result
}

/**
 * The supports the multi-edit has to leave alone: a support config without a
 * support compression check is no real support (e.g. the second support config
 * of a column) — it has no utilization to show, so it is left out of the matrix
 * and never written on save. A member without a single support check carries
 * nothing to judge this by (its checks are simply not computed yet), so all of
 * its supports stay in the edit.
 */
export const buildIrrelevantSupports = (
  elements: MultiEditCheckSettingsElement[],
): SupportIndicesByElement => {
  const irrelevantSupports: SupportIndicesByElement = {}

  for (const element of elements) {
    const supportConfigs = getSupportConfigs(element.checkSettings) ?? []
    const checks = mapSupportCompressionChecks(element.checks, supportConfigs)
    if (checks.every(check => check === undefined)) continue

    const indices = supportConfigs.flatMap((_, index) => (checks[index] ? [] : [index]))
    if (indices.length > 0) irrelevantSupports[element.guid] = indices
  }

  return irrelevantSupports
}

/** True while the staged fields apply to this support. */
export const isSupportEditable = (
  shared: SharedSupportCompressionSettings | undefined,
  elementGuid: string,
  supportIndex: number,
): boolean =>
  !containsSupportIndex(shared?.excludedSupports, elementGuid, supportIndex) &&
  !containsSupportIndex(shared?.irrelevantSupports, elementGuid, supportIndex)

/**
 * One support's value for a field, normalized so that "not explicitly given"
 * always reads null — the persisted configs use null and undefined
 * interchangeably for area/overhang/fc90k.
 */
export const getSupportConfigFieldValue = (
  config: SupportConfig,
  field: SupportCompressionField,
): SupportCompressionFieldValue => {
  if (field === 'k_c_90') return config.k_c_90
  return config[field] ?? null
}

/**
 * The common value for one field when every support of every selected member
 * agrees on it, `{ isShared: false }` while they differ. Supports that are no
 * real supports never take part. A selection without a single support has
 * nothing to edit and counts as not shared.
 */
export const getSharedSupportFieldValue = (
  elements: MultiEditCheckSettingsElement[],
  field: SupportCompressionField,
  irrelevantSupports?: SupportIndicesByElement,
): { isShared: boolean; value?: SupportCompressionFieldValue } => {
  const values = elements.flatMap(element =>
    (getSupportConfigs(element.checkSettings) ?? []).flatMap((config, index) =>
      containsSupportIndex(irrelevantSupports, element.guid, index)
        ? []
        : [getSupportConfigFieldValue(config, field)],
    ),
  )
  if (values.length === 0) return { isShared: false }

  const [reference, ...rest] = values
  return rest.every(value => isEqual(value, reference))
    ? { isShared: true, value: reference }
    : { isShared: false }
}

/**
 * Seeds the form: each field the whole selection agrees on is staged with that
 * common value, differing fields stay absent until „Ändern" stages them.
 */
export const buildSharedSupportCompressionDefaults = (
  elements: MultiEditCheckSettingsElement[],
  irrelevantSupports?: SupportIndicesByElement,
): SharedSupportCompressionSettings => {
  const defaults: Partial<Record<SupportCompressionField, SupportCompressionFieldValue>> = {}
  for (const field of SUPPORT_COMPRESSION_FIELDS) {
    const { isShared, value } = getSharedSupportFieldValue(elements, field, irrelevantSupports)
    if (isShared) defaults[field] = value
  }
  return defaults as SharedSupportCompressionSettings
}

/**
 * What „Ändern" stages: the first value the edit still covers, i.e. the first
 * support of the first member that has an editable one left.
 */
export const getFirstSupportFieldValue = (
  elements: MultiEditCheckSettingsElement[],
  field: SupportCompressionField,
  shared?: SharedSupportCompressionSettings,
): SupportCompressionFieldValue | undefined => {
  for (const element of elements) {
    const supportConfigs = getSupportConfigs(element.checkSettings) ?? []
    const config = supportConfigs.find((_, index) => isSupportEditable(shared, element.guid, index))
    if (config) return getSupportConfigFieldValue(config, field)
  }
  return undefined
}

/** True while at least one support of the selection still takes part in the edit. */
export const hasIncludedSupport = (
  elements: MultiEditCheckSettingsElement[],
  shared: SharedSupportCompressionSettings | undefined,
): boolean =>
  elements.some(element =>
    (getSupportConfigs(element.checkSettings) ?? []).some((_, index) =>
      isSupportEditable(shared, element.guid, index),
    ),
  )

/**
 * Removes every support of every member from the edit, so the user can pick the
 * few that should take part — or puts them all back once none is left.
 */
export const toggleAllExcludedSupports = (
  shared: SharedSupportCompressionSettings | undefined,
  elements: MultiEditCheckSettingsElement[],
): SupportIndicesByElement => {
  if (!hasIncludedSupport(elements, shared)) return {}

  const excludedSupports: SupportIndicesByElement = {}

  for (const element of elements) {
    const indices = (getSupportConfigs(element.checkSettings) ?? []).flatMap((_, index) =>
      containsSupportIndex(shared?.irrelevantSupports, element.guid, index) ? [] : [index],
    )
    if (indices.length > 0) excludedSupports[element.guid] = indices
  }

  return excludedSupports
}

/**
 * True when no member has support `supportIndex` left in the edit — the state
 * the matrix' column header toggles back and forth. Members without a real
 * support at that index never count.
 */
export const isSupportIndexExcluded = (
  elements: MultiEditCheckSettingsElement[],
  shared: SharedSupportCompressionSettings | undefined,
  supportIndex: number,
): boolean => {
  const elementsWithSupport = elements.filter(
    element =>
      (getSupportConfigs(element.checkSettings)?.length ?? 0) > supportIndex &&
      !containsSupportIndex(shared?.irrelevantSupports, element.guid, supportIndex),
  )

  return (
    elementsWithSupport.length > 0 &&
    elementsWithSupport.every(element =>
      containsSupportIndex(shared?.excludedSupports, element.guid, supportIndex),
    )
  )
}

/**
 * Removes support `supportIndex` from every member that has a real one at that
 * index — or adds it back everywhere once no member has it left.
 */
export const toggleExcludedSupportIndex = (
  shared: SharedSupportCompressionSettings | undefined,
  elements: MultiEditCheckSettingsElement[],
  supportIndex: number,
): SupportIndicesByElement => {
  const shouldExclude = !isSupportIndexExcluded(elements, shared, supportIndex)

  return elements.reduce((result, element) => {
    if ((getSupportConfigs(element.checkSettings)?.length ?? 0) <= supportIndex) return result
    if (containsSupportIndex(shared?.irrelevantSupports, element.guid, supportIndex)) return result

    return containsSupportIndex(result, element.guid, supportIndex) === shouldExclude
      ? result
      : toggleExcludedSupport(result, element.guid, supportIndex)
  }, shared?.excludedSupports ?? {})
}

/** True once at least one field is staged — saving before that would be a no-op. */
export const hasStagedSupportCompressionField = (
  shared: SharedSupportCompressionSettings | undefined,
): boolean => !!shared && SUPPORT_COMPRESSION_FIELDS.some(field => shared[field] !== undefined)

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

/**
 * True while every staged field is complete enough to compute with — a
 * half-filled area/overhang (empty inputs read null or '') must block the live
 * recompute and the save until the user finishes typing.
 */
export const isSupportCompressionStageValid = (
  shared: SharedSupportCompressionSettings | undefined,
): boolean => {
  if (!shared) return true
  const { area, overhang, k_c_90, f_c_90_k } = shared

  if (
    area != null &&
    !(
      isFiniteNumber(area.length) &&
      area.length > 0 &&
      isFiniteNumber(area.width) &&
      area.width > 0
    )
  ) {
    return false
  }

  if (
    overhang != null &&
    ![overhang.left, overhang.right, overhang.front, overhang.back].every(
      value => isFiniteNumber(value) && value >= 0,
    )
  ) {
    return false
  }

  if (k_c_90 !== undefined && !isFiniteNumber(k_c_90)) return false

  if (f_c_90_k != null && !(isFiniteNumber(f_c_90_k) && f_c_90_k > 0)) return false

  return true
}

/**
 * True when the staged fields would leave some support with neither area nor
 * overhang. The backend rejects such a config outright
 * (SupportConfig.validate_at_least_one_of_area_or_overhang), so the live
 * recompute and the save must be blocked — the same rule the single-edit
 * schema enforces. Only staging area/overhang can cause this: persisted
 * configs always satisfy the rule, so kc90/fc90k-only edits never trip it.
 * Supports the edit does not touch keep their saved values and cannot go bare.
 */
export const wouldLeaveSupportWithoutAreaAndOverhang = (
  elements: MultiEditCheckSettingsElement[],
  shared: SharedSupportCompressionSettings | undefined,
): boolean => {
  if (!shared) return false

  const areaStaged = shared.area !== undefined
  const overhangStaged = shared.overhang !== undefined
  if (!areaStaged && !overhangStaged) return false

  return elements.some(element =>
    (getSupportConfigs(element.checkSettings) ?? []).some((config, index) => {
      if (!isSupportEditable(shared, element.guid, index)) return false

      const area = areaStaged ? shared.area : config.area ?? null
      const overhang = overhangStaged ? shared.overhang : config.overhang ?? null
      return area == null && overhang == null
    }),
  )
}

/**
 * Gate for the live recompute and the save: every staged field is complete and
 * no support would be stripped of both area and overhang.
 */
export const isSupportCompressionFormValid = (
  shared: SharedSupportCompressionSettings | undefined,
  elements: MultiEditCheckSettingsElement[],
): boolean =>
  isSupportCompressionStageValid(shared) &&
  !wouldLeaveSupportWithoutAreaAndOverhang(elements, shared)

/**
 * Whether the matrix should recompute its preview: only a staged field changes
 * anything on the members, so removing supports alone (or an invalid stage) must
 * not fire a round of requests that would return the saved values anyway.
 */
export const shouldPreviewSupportCompression = (
  shared: SharedSupportCompressionSettings | undefined,
  elements: MultiEditCheckSettingsElement[],
): boolean =>
  hasStagedSupportCompressionField(shared) &&
  hasIncludedSupport(elements, shared) &&
  isSupportCompressionFormValid(shared, elements)

/**
 * Applies the staged fields to every support of one member the edit covers,
 * leaving every other support field — and the rest of its check settings —
 * untouched. Only fields present on the staged object are written: a field the
 * supports still differ on (and that was never staged via „Ändern") keeps its
 * per-support values, and so does every removed or non-applicable support.
 */
export const mergeSupportCompressionSettings = (
  existing: SettingsOnMember,
  shared: SharedSupportCompressionSettings,
): SettingsOnMember => {
  if (!hasSupportCompressionSettings(existing)) return existing
  if (!hasStagedSupportCompressionField(shared)) return existing

  return {
    ...existing,
    support_configs: existing.support_configs.map((config, index) =>
      isSupportEditable(shared, existing.element_guid, index)
        ? {
            ...config,
            ...(shared.area !== undefined ? { area: shared.area } : {}),
            ...(shared.overhang !== undefined ? { overhang: shared.overhang } : {}),
            ...(shared.k_c_90 !== undefined ? { k_c_90: shared.k_c_90 } : {}),
            ...(shared.f_c_90_k !== undefined ? { f_c_90_k: shared.f_c_90_k } : {}),
          }
        : config,
    ),
  }
}

/**
 * How many support columns the matrix needs: the widest selected member, counted
 * up to its last real support so a trailing non-applicable support adds no
 * column at all.
 */
export const getMaxSupportCount = (
  elements: MultiEditCheckSettingsElement[],
  irrelevantSupports?: SupportIndicesByElement,
): number =>
  elements.reduce(
    (max, element) =>
      (getSupportConfigs(element.checkSettings) ?? []).reduce(
        (count, _, index) =>
          containsSupportIndex(irrelevantSupports, element.guid, index)
            ? count
            : Math.max(count, index + 1),
        max,
      ),
    0,
  )

/** What one cell of the matrix shows for a support. */
export type SupportCellState = 'editable' | 'removed' | 'not-applicable'

/**
 * The state of every matrix column for one member: supports the member does not
 * have and supports that are no real supports are not applicable and stay empty,
 * the user's removals show as removed.
 */
export const getSupportCellStates = (
  element: MultiEditCheckSettingsElement,
  shared: SharedSupportCompressionSettings | undefined,
  supportCount: number,
): SupportCellState[] => {
  const configCount = getSupportConfigs(element.checkSettings)?.length ?? 0

  return Array.from({ length: supportCount }, (_, index) => {
    if (index >= configCount) return 'not-applicable'
    if (containsSupportIndex(shared?.irrelevantSupports, element.guid, index)) {
      return 'not-applicable'
    }
    return containsSupportIndex(shared?.excludedSupports, element.guid, index)
      ? 'removed'
      : 'editable'
  })
}

/** Column header for support i, matching how engineers number supports. */
export const supportColumnLabel = (supportIndex: number): string => `A${supportIndex + 1}`
